import { useState, useEffect, useRef } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { compressImage } from '../../../lib/imageCompression'
import { Lock, Upload, Trash2, Plus, Check, AlertCircle, X, ChevronDown, Copy, ExternalLink, Share2, Shirt } from 'lucide-react'
import PayoutAccountSetup from '../../../components/PayoutAccountSetup'
import { useNavigate } from 'react-router-dom'
import AsoEbiOrdersTable from './AsoEbiOrdersTable'
import PageTip from '../../../components/PageTip'

// ── Decorative elements ───────────────────────────────────────
function KenteStrip() {
  const colors = ['#C4826A','#F4B300','#C4826A','#1A1A1A','#F4B300','#C4826A','#9BAF9B','#F4B300','#C4826A','#1A1A1A','#F4B300','#C4826A']
  return <div style={{ display:'flex', height:6, borderRadius:'4px 4px 0 0', overflow:'hidden' }}>{colors.map((col,i)=><div key={i} style={{ flex:1, background:col }}/>)}</div>
}

function AdinkraMark({ size=18, color='#C4826A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke={color} strokeWidth="1.2" fill="none"/>
      <circle cx="10" cy="10" r="4" stroke={color} strokeWidth="1" fill="none"/>
      <line x1="10" y1="2" x2="10" y2="18" stroke={color} strokeWidth="0.8" strokeLinecap="round"/>
      <line x1="2" y1="10" x2="18" y2="10" stroke={color} strokeWidth="0.8" strokeLinecap="round"/>
      <circle cx="10" cy="10" r="1.5" fill={color}/>
    </svg>
  )
}

function PremiumGate({ navigate, title = 'Unlock Full Aso Ebi Hub', desc = 'Manage orders, track payments, coordinate collection — everything in one place.' }) {
  return (
    <div style={{ background:'rgba(244,179,0,0.05)', border:'1.5px dashed rgba(244,179,0,0.3)', borderRadius:'1rem', padding:'1.5rem', textAlign:'center' }}>
      <Lock size={20} color="#F4B300" style={{ marginBottom:'0.5rem' }}/>
      <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>{title}</p>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 1rem', lineHeight:1.7 }}>{desc}</p>
      <button onClick={()=>navigate('/pricing')} style={{ background:'linear-gradient(135deg,#C4826A,#F4B300)', color:'white', border:'none', borderRadius:100, padding:'0.625rem 1.5rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem' }}>Upgrade to Premium</button>
    </div>
  )
}

const STATUS_COLORS = {
  pending:   { bg:'rgba(220,38,38,0.08)',   color:'#DC2626' },
  paid:      { bg:'rgba(244,179,0,0.12)',   color:'#B8920A' },
  collected: { bg:'rgba(155,175,155,0.18)', color:'#5A7A5A' },
  cancelled: { bg:'#F3F0EA',               color:'#BCBCBC' },
}

// ── CSV download for collection point ─────────────────────────
function downloadPointCSV(point, orders, cs = '₦') {
  const pointOrders = orders.filter(o => o.collection_point === point.name)
  const header = `Collection Point: ${point.name}\nGenerated: ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}\n\n`
  const cols = ['GUEST NAME','PHONE','YARDS','AMOUNT','STATUS'].join(',')
  const rows = pointOrders.map(o => [
    `"${o.guest_name||''}"`,
    `"${o.guest_phone||'—'}"`,
    `"${o.yards||0} yds"`,
    `"${cs}${(o.amount||0).toLocaleString()}"`,
    `"${o.status}"`,
  ].join(','))
  const total = pointOrders.reduce((s,o)=>s+(o.yards||0),0)
  const paid  = pointOrders.filter(o=>o.status==='paid'||o.status==='collected').length
  const footer = `\n\n"Total Guests:","${pointOrders.length}"\n"Total Yards:","${total}"\n"Total Paid:","${paid}"`
  const csv = header + cols + '\n' + rows.join('\n') + footer
  const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.setAttribute('download', `${point.name.replace(/\s+/g,'-').toLowerCase()}-guests.csv`)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

// ── Tab label style helper ────────────────────────────────────
const tabStyle = (active) => ({
  flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem',
  fontWeight: active ? 700 : 500,
  color: active ? '#B36B55' : '#8C7B70',
  background: active ? 'white' : 'transparent',
  border:'none',
  borderBottom: active ? '2.5px solid #B36B55' : '2.5px solid transparent',
  padding:'0.75rem 0.5rem', cursor:'pointer', transition:'all 0.15s',
})

// ── Main page ─────────────────────────────────────────────────
export default function AsoEbiPage() {
  const { wedding, isPremium } = useWedding()
  const CURRENCY_SYMBOLS = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh', ZAR:'R' }
  const cs = CURRENCY_SYMBOLS[wedding?.currency] || '₦'
  const navigate     = useNavigate()

  // Fabric setup state
  const [setup, setSetup]     = useState(null)
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)
  const [fabric, setFabric]   = useState({ name:'', vendor_name:'', vendor_contact:'', price_per_yard:'', min_yards:'', order_deadline:'' })

  // Lookbook state
  const [photos, setPhotos]       = useState([])
  const [uploading, setUploading] = useState(false)
  const [activeCategory, setCategory] = useState('swatch')
  const photoRef = useRef()

  // Orders state
  const [orders, setOrders]           = useState([])
  const [updatingOrder, setUpdatingOrder] = useState(null)
  const [ambassadors, setAmbassadors] = useState([])

  // Collection points state
  const [points, setPoints]       = useState([])
  const [addingPoint, setAddingPoint] = useState(false)
  const [newPoint, setNewPoint]   = useState({ name:'', address:'', contact_person:'', contact_phone:'', available_dates:'' })

  // UI state
  const [briefOpen, setBriefOpen] = useState(true)
  const [briefTab, setBriefTab]   = useState('fabric')
  const [copied, setCopied]       = useState(false)

  const guestPortalLink = wedding ? `${window.location.origin}/w/${wedding.slug}` : ''
  const asoEbiLink      = guestPortalLink ? `${guestPortalLink}?tab=asoebi` : ''

  const coupleNames = [wedding?.partner1_name, wedding?.partner2_name].filter(Boolean).join(' & ')
  const shareMessage = `You're invited to wear Aso Ebi for ${coupleNames || 'our'} wedding! 🎉✨\n\nPick your fabric, choose your yards and pay securely — all from this link. We can't wait to celebrate this special day with you 💛`

  function copyLink() { navigator.clipboard.writeText(asoEbiLink); setCopied(true); setTimeout(()=>setCopied(false), 2000) }
  async function shareLink() {
    if (navigator.share) {
      try { await navigator.share({ title:`Aso Ebi · ${coupleNames || 'Our Wedding'}`, text: shareMessage, url:asoEbiLink }) } catch(_) {}
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage + '\n' + asoEbiLink)}`, '_blank')
    }
  }

  useEffect(() => {
    if (!wedding?.id) return
    fetchSetup()
    fetchAmbassadors()
    if (isPremium) { fetchPhotos(); fetchOrders(); fetchPoints() }
  }, [wedding?.id, isPremium])

  useEffect(() => {
    if (!wedding?.id) return
    const onFocus = () => fetchAmbassadors()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [wedding?.id])

  async function fetchSetup() {
    const { data } = await supabase.from('asoebi_setup').select('*').eq('wedding_id', wedding.id).maybeSingle()
    if (data) {
      setSetup(data)
      setFabric({ name:data.fabric_name||'', vendor_name:data.vendor_name||'', vendor_contact:data.vendor_contact||'', price_per_yard:data.price_per_yard||'', min_yards:data.min_yards||'', order_deadline:data.order_deadline?.slice(0,10)||'' })
    }
  }

  async function fetchPhotos() {
    const { data } = await supabase.from('asoebi_photos').select('*').eq('wedding_id', wedding.id).order('sort_order')
    setPhotos(data||[])
  }

  async function fetchOrders() {
    const { data } = await supabase.from('asoebi_orders').select('*').eq('wedding_id', wedding.id).order('created_at',{ascending:false})
    setOrders(data||[])
  }

  async function fetchPoints() {
    const { data } = await supabase.from('asoebi_collection_points').select('*').eq('wedding_id', wedding.id).order('created_at')
    setPoints(data||[])
  }

  async function fetchAmbassadors() {
    const { data } = await supabase.from('asoebi_ambassadors').select('*').eq('wedding_id', wedding.id).order('created_at')
    setAmbassadors(data||[])
  }

  async function saveSetup() {
    if (!wedding?.id) return
    setSaving(true)
    const payload = { wedding_id:wedding.id, fabric_name:fabric.name, vendor_name:fabric.vendor_name, vendor_contact:fabric.vendor_contact, price_per_yard:fabric.price_per_yard?Number(fabric.price_per_yard):null, min_yards:fabric.min_yards?Number(fabric.min_yards):null, order_deadline:fabric.order_deadline||null, is_active:true }
    if (setup?.id) { await supabase.from('asoebi_setup').update(payload).eq('id', setup.id) }
    else { const { data } = await supabase.from('asoebi_setup').insert(payload).select().single(); setSetup(data) }
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false), 2500); fetchSetup()
  }

  async function uploadPhoto(e) {
    const file = e.target.files?.[0]; if (!file || !wedding?.id) return
    setUploading(true)
    const compressed = await compressImage(file)
    const path = `${wedding.id}/asoebi/${activeCategory}-${Date.now()}.webp`
    const { error } = await supabase.storage.from('wedding-photos').upload(path, compressed)
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from('wedding-photos').getPublicUrl(path)
      await supabase.from('asoebi_photos').insert({ wedding_id:wedding.id, url:publicUrl, storage_path:path, type:activeCategory, sort_order:photos.length })
      fetchPhotos()
    }
    setUploading(false); e.target.value = ''
  }

  async function deletePhoto(photo) {
    await supabase.storage.from('wedding-photos').remove([photo.storage_path])
    await supabase.from('asoebi_photos').delete().eq('id', photo.id)
    fetchPhotos()
  }

  async function addPoint() {
    if (!newPoint.name.trim()) return
    await supabase.from('asoebi_collection_points').insert({ wedding_id:wedding.id, ...newPoint })
    setNewPoint({ name:'', address:'', contact_person:'', contact_phone:'', available_dates:'' })
    setAddingPoint(false); fetchPoints()
  }

  async function deletePoint(id) {
    await supabase.from('asoebi_collection_points').delete().eq('id', id); fetchPoints()
  }

  async function updateOrderStatus(order, status) {
    setUpdatingOrder(order.id)
    await supabase.from('asoebi_orders').update({ status }).eq('id', order.id)
    fetchOrders(); setUpdatingOrder(null)
  }

  async function sendReminderForOrders(selectedOrders) {
    const msg = `Hi! A reminder about your Aso Ebi order for ${wedding.partner1_name} & ${wedding.partner2_name}'s wedding. Please complete your payment to confirm your order. 🧵`
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
  }

  if (!wedding) return <div style={{ textAlign:'center', padding:'3rem' }}><p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading…</p></div>

  const CATEGORIES = [
    { id:'swatch', label:'Fabric Swatch', limit:1 },
    { id:'style_women', label:"Women's Styles", limit:5 },
    { id:'style_men', label:"Men's Styles", limit:5 },
    { id:'do_not_sew', label:'Do Not Sew ✕', limit:3 },
  ]
  const catPhotos = photos.filter(p => p.type === activeCategory)
  const catLimit  = CATEGORIES.find(c => c.id === activeCategory)?.limit || 5
  const totalYards  = orders.filter(o=>o.status!=='cancelled').reduce((s,o)=>s+(o.yards||0),0)
  const totalPaid   = orders.filter(o=>o.status==='paid'||o.status==='collected').reduce((s,o)=>s+(o.amount||0),0)
  const daysLeft    = fabric.order_deadline ? Math.ceil((new Date(fabric.order_deadline)-new Date())/(1000*60*60*24)) : null
  const pendingCount = orders.filter(o=>o.status==='pending').length

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>

      <PageTip id="asoebi" title="Aso Ebi made easy"
        points={[
          'Set your fabric, price and collection points.',
          'Guests order and pay through your wedding portal.',
          'Track every yard ordered and naira collected.',
        ]}/>

      {/* ── Header ── */}
      <div style={{ background:'linear-gradient(135deg,#FAF5EC 0%,#F5EAD8 100%)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'1.25rem', overflow:'hidden' }}>
        <KenteStrip/>
        <div style={{ padding:'1.5rem 1.5rem 1.25rem' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'1rem' }}>
            <div>
              <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.5rem,3vw,2rem)', color:'#1A1A1A', margin:'0 0 0.3rem', letterSpacing:'-0.03em' }}>Aso Ebi Hub</h1>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:0 }}>Your aso-ebi game is about to be stress-free.</p>
            </div>
            {setup?.is_active && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:700, color:'#9BAF9B', background:'rgba(155,175,155,0.12)', borderRadius:100, padding:'4px 12px', border:'1px solid rgba(155,175,155,0.3)' }}>● Active</span>}
          </div>
          {setup && (
            <div style={{ marginTop:'1.25rem', borderTop:'1px solid rgba(196,130,106,0.15)', paddingTop:'1.25rem' }}>
              <div style={{ display:'grid', gridTemplateColumns:'40% 30% 30%', gap:'0 0' }}>
                {/* Row 1 */}
                <div style={{ paddingRight:'0.5rem', paddingBottom:'0.75rem' }}>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{orders.length}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#9B9B9B', margin:'0.2rem 0 0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Orders</p>
                </div>
                <div style={{ paddingRight:'0.5rem', paddingBottom:'0.75rem' }}>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{totalYards||'—'}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#9B9B9B', margin:'0.2rem 0 0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Total Yards</p>
                </div>
                <div style={{ paddingBottom:'0.75rem' }}>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{orders.filter(o=>o.status==='paid'||o.status==='collected').length}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#9B9B9B', margin:'0.2rem 0 0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Confirmed Paid</p>
                </div>
                {/* Row 2 — same columns, always aligned */}
                <div style={{ paddingRight:'0.5rem', borderTop:'1px solid rgba(196,130,106,0.1)', paddingTop:'0.75rem' }}>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{totalPaid>0?`${cs}${totalPaid.toLocaleString()}`:`${cs}0`}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#9B9B9B', margin:'0.2rem 0 0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Revenue</p>
                </div>
                <div style={{ paddingRight:'0.5rem', borderTop:'1px solid rgba(196,130,106,0.1)', paddingTop:'0.75rem' }}>
                  {daysLeft !== null ? (
                    <>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>
                        <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:daysLeft<=7?'#C4826A':'#1A1A1A', margin:0, lineHeight:1 }}>{daysLeft>0?`${daysLeft}d`:'—'}</p>
                        {daysLeft<=7 && daysLeft>0 && <span style={{ width:7, height:7, borderRadius:'50%', background:'#C4826A', display:'inline-block', animation:'pulse 1.5s ease-in-out infinite' }}/>}
                      </div>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:daysLeft<=7?'#C4826A':'#9B9B9B', margin:'0.2rem 0 0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Days Left</p>
                    </>
                  ) : <div/>}
                </div>
                <div style={{ borderTop:'1px solid rgba(196,130,106,0.1)', paddingTop:'0.75rem' }}>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{orders.filter(o=>o.status==='collected').length}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#9B9B9B', margin:'0.2rem 0 0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Collected</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Aso Ebi Guest Link — compact */}
      <div style={{ background:'white', border:'1px solid #E8E0D8', borderRadius:'0.875rem', padding:'0.875rem 1rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
        <div style={{ width:36, height:36, background:'rgba(196,130,106,0.1)', borderRadius:'0.625rem', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Shirt size={16} color="#C4826A"/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#1A1A1A', margin:'0 0 0.1rem' }}>Guest Aso Ebi link</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#BCBCBC', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{asoEbiLink}</p>
        </div>
        <div style={{ display:'flex', gap:'0.375rem', flexShrink:0 }}>
          <button onClick={copyLink} title="Copy link" style={{ width:32, height:32, background:copied?'rgba(155,175,155,0.12)':'#F3F0EA', border:`1px solid ${copied?'rgba(155,175,155,0.4)':'#E5E0D8'}`, borderRadius:'0.5rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:copied?'#9BAF9B':'#6B6B6B', transition:'all 0.15s' }}>
            {copied ? <Check size={13}/> : <Copy size={13}/>}
          </button>
          <button onClick={shareLink} title="Share" style={{ width:32, height:32, background:'#F3F0EA', border:'1px solid #E5E0D8', borderRadius:'0.5rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#C4826A' }}>
            <Share2 size={13}/>
          </button>
          <a href={asoEbiLink} target="_blank" rel="noopener noreferrer" style={{ width:32, height:32, background:'#F3F0EA', border:'1px solid #E5E0D8', borderRadius:'0.5rem', display:'flex', alignItems:'center', justifyContent:'center', color:'#6B6B6B', textDecoration:'none' }}>
            <ExternalLink size={13}/>
          </a>
        </div>
      </div>

      {/* ── Payout account (one account for Aso Ebi + cash gifts) ── */}
      <PayoutAccountSetup variant="asoebi"/>

      {/* ── THE ASO EBI BRIEF — unified 3-tab card ── */}
      <div style={{ background:'#FEFCFA', border:'1px solid #E8E0D8', borderRadius:'1.25rem', overflow:'hidden' }}>
        {/* Collapsible header */}
        <button onClick={()=>setBriefOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1.25rem 1.5rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', gap:'0.5rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
            <AdinkraMark size={16}/>
            <div>
              <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#1A1A1A', margin:0 }}>The Aso Ebi Brief</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:0 }}>Fabric details, style inspiration, and collection points</p>
            </div>
          </div>
          <ChevronDown size={16} color="#9B9B9B" style={{ transform:briefOpen?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.3s ease', flexShrink:0 }}/>
        </button>

        {briefOpen && (
          <div style={{ borderTop:'1px solid #E8E0D8' }}>
            {/* Tab bar */}
            <div style={{ display:'flex', background:'#F5F0EA', borderBottom:'1px solid #E8E0D8' }}>
              {[
                { id:'fabric',   label:'Fabric Details' },
                { id:'lookbook', label:'Styles & Lookbook' },
                { id:'points',   label:'Collection Points' },
              ].map(t=>(
                <button key={t.id} onClick={()=>setBriefTab(t.id)} style={tabStyle(briefTab===t.id)}>{t.label}</button>
              ))}
            </div>

            {/* Tab content */}
            <div style={{ padding:'1.5rem' }}>

              {/* ── TAB 1: Fabric Details ── */}
              {briefTab==='fabric' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'0.875rem' }}>
                    {[
                      { label:'Fabric Name', key:'name', placeholder:'e.g. Asooke, George, Ankara' },
                      { label:'Contact Name', key:'vendor_name', placeholder:'e.g. Aunty Bisi, Coordinator' },
                      { label:'Contact Phone / WhatsApp', key:'vendor_contact', placeholder:'e.g. 08012345678' },
                    ].map(f=>(
                      <div key={f.key}>
                        <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.04em' }}>{f.label}</label>
                        <input className="input-field" placeholder={f.placeholder} value={fabric[f.key]} onChange={e=>setFabric(p=>({...p,[f.key]:e.target.value}))} style={{ marginTop:'0.3rem' }}/>
                      </div>
                    ))}
                    <div>
                      <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.04em' }}>Price Per Yard ({cs})</label>
                      <input className="input-field" type="number" placeholder="e.g. 3000" value={fabric.price_per_yard} onChange={e=>setFabric(p=>({...p,price_per_yard:e.target.value}))} style={{ marginTop:'0.3rem' }}/>
                    </div>
                    <div>
                      <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.04em' }}>Min. Yards Per Person</label>
                      <input className="input-field" type="number" placeholder="e.g. 6" value={fabric.min_yards} onChange={e=>setFabric(p=>({...p,min_yards:e.target.value}))} style={{ marginTop:'0.3rem' }}/>
                    </div>
                    <div>
                      <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.04em' }}>Order Deadline</label>
                      <input className="input-field" type="date" value={fabric.order_deadline} onChange={e=>setFabric(p=>({...p,order_deadline:e.target.value}))} style={{ marginTop:'0.3rem' }}/>
                    </div>
                  </div>
                  <div style={{ background:'rgba(244,179,0,0.06)', border:'1px solid rgba(244,179,0,0.2)', borderRadius:'0.75rem', padding:'0.75rem 1rem', display:'flex', gap:'0.5rem', alignItems:'flex-start' }}>
                    <AlertCircle size={15} color="#F4B300" style={{ flexShrink:0, marginTop:2 }}/>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:0, lineHeight:1.65 }}>
                      Guests pay for their fabric online and it settles straight to your bank. Set the account it lands in using <strong>“Receive Aso Ebi payments”</strong> at the top of this page.
                    </p>
                  </div>
                  <button onClick={saveSetup} disabled={saving} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
                    {saved ? <><Check size={14}/>Saved!</> : saving ? 'Saving…' : 'Save Fabric Details'}
                  </button>
                </div>
              )}

              {/* ── TAB 2: Styles & Lookbook ── */}
              {briefTab==='lookbook' && (
                !isPremium ? <PremiumGate navigate={navigate}/> : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                    <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                      {CATEGORIES.map(cat=>(
                        <button key={cat.id} onClick={()=>setCategory(cat.id)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:600, padding:'0.4rem 0.875rem', borderRadius:100, border:'1.5px solid', cursor:'pointer', transition:'all 0.15s', background:activeCategory===cat.id?(cat.id==='do_not_sew'?'#1A1A1A':'rgba(196,130,106,0.1)'):'white', borderColor:activeCategory===cat.id?(cat.id==='do_not_sew'?'#1A1A1A':'#C4826A'):'#E5E0D8', color:activeCategory===cat.id?(cat.id==='do_not_sew'?'white':'#C4826A'):'#6B6B6B' }}>
                          {cat.label} ({photos.filter(p=>p.type===cat.id).length}/{cat.limit})
                        </button>
                      ))}
                    </div>
                    {activeCategory==='do_not_sew' && (
                      <div style={{ background:'rgba(28,20,16,0.05)', border:'1px solid rgba(28,20,16,0.1)', borderRadius:'0.75rem', padding:'0.75rem 1rem', display:'flex', gap:'0.5rem', alignItems:'flex-start' }}>
                        <AlertCircle size={15} color="#1A1A1A" style={{ flexShrink:0, marginTop:1 }}/>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:0, lineHeight:1.65 }}>Upload styles you do <strong>not</strong> want guests wearing. These appear with an ✕ on the guest portal.</p>
                      </div>
                    )}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:'0.75rem' }}>
                      {catPhotos.map(photo=>(
                        <div key={photo.id} style={{ position:'relative', borderRadius:'0.875rem', overflow:'hidden', aspectRatio:'4/5', background:'#F3F0EA' }}>
                          <img src={photo.url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                          {activeCategory==='do_not_sew' && <div style={{ position:'absolute', inset:0, background:'rgba(28,20,16,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontSize:'2rem', color:'white', fontWeight:900 }}>✕</span></div>}
                          <button onClick={()=>deletePhoto(photo)} style={{ position:'absolute', top:6, right:6, width:26, height:26, background:'rgba(28,20,16,0.7)', border:'none', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}><Trash2 size={12} color="white"/></button>
                        </div>
                      ))}
                      {catPhotos.length < catLimit && (
                        <button onClick={()=>photoRef.current?.click()} style={{ border:'2px dashed #E5E0D8', borderRadius:'0.875rem', aspectRatio:'4/5', background:'none', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.4rem', cursor:'pointer' }}>
                          <Upload size={18} color="#BCBCBC"/>
                          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#BCBCBC' }}>{uploading?'Uploading…':'Add Photo'}</span>
                        </button>
                      )}
                    </div>
                    <input ref={photoRef} type="file" accept="image/*" style={{ display:'none' }} onChange={uploadPhoto}/>
                  </div>
                )
              )}

              {/* ── TAB 3: Collection Points ── */}
              {briefTab==='points' && (
                !isPremium ? <PremiumGate navigate={navigate}/> : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                    {points.map(pt=>(
                      <div key={pt.id} style={{ background:'rgba(244,179,0,0.04)', border:'1px solid rgba(244,179,0,0.15)', borderRadius:'0.875rem', padding:'1rem 1.125rem' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
                          <div>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'#1A1A1A', margin:'0 0 0.2rem' }}>{pt.name}</p>
                            {pt.address && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:'0 0 0.15rem' }}>{pt.address}</p>}
                            {pt.contact_person && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 0.15rem' }}>Contact: {pt.contact_person}{pt.contact_phone ? ` · ${pt.contact_phone}` : ''}</p>}
                            {pt.available_dates && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:0 }}>Available: {pt.available_dates}</p>}
                          </div>
                          <button onClick={()=>deletePoint(pt.id)} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#BCBCBC', flexShrink:0 }}><X size={14}/></button>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:'0.5rem', borderTop:'1px solid rgba(244,179,0,0.1)' }}>
                          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B' }}>
                            {orders.filter(o=>o.collection_point===pt.name).length} guests assigned
                          </span>
                          <button onClick={()=>downloadPointCSV(pt, orders, cs)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#C4826A', background:'rgba(196,130,106,0.08)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:100, padding:'4px 12px', cursor:'pointer' }}>
                            ↓ Download Guest List
                          </button>
                        </div>
                      </div>
                    ))}

                    {addingPoint ? (
                      <div style={{ background:'#FAF5EC', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.875rem', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.625rem' }}>
                        {[
                          { key:'name', placeholder:'Location name *' },
                          { key:'address', placeholder:'Full location name on Google Maps', style:{ color:'rgba(107,107,107,0.5)' } },
                          { key:'available_dates', placeholder:'e.g. Sat 10 May, 10am–4pm' },
                          { key:'contact_person', placeholder:'Coordinator name' },
                          { key:'contact_phone', placeholder:'Coordinator phone / WhatsApp' },
                        ].map(f=>(
                          <input key={f.key} className="input-field" placeholder={f.placeholder} value={newPoint[f.key]} onChange={e=>setNewPoint(p=>({...p,[f.key]:e.target.value}))} style={{ fontSize:'0.82rem' }}/>
                        ))}
                        <div style={{ display:'flex', gap:'0.5rem' }}>
                          <button onClick={addPoint} className="btn-primary" style={{ fontSize:'0.8rem', padding:'0.55rem 1rem' }}>Save Collection Point</button>
                          <button onClick={()=>setAddingPoint(false)} className="btn-secondary" style={{ fontSize:'0.8rem', padding:'0.55rem 1rem' }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={()=>setAddingPoint(true)} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'1.5px dashed rgba(196,130,106,0.4)', borderRadius:'0.75rem', padding:'0.625rem 1rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#C4826A', fontWeight:600 }}>
                        <Plus size={15}/> {points.length > 0 ? 'Add Another Collection Point' : 'Add Collection Point'}
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── GUEST ORDERS ── */}
      <div className="card" style={{ padding:'1.5rem' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'1.25rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
            <AdinkraMark size={16}/>
            <div>
              <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', color:'#1A1A1A', margin:0 }}>Guest Orders</h2>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:0 }}>Every naira collected, every yard ordered — tracked automatically.</p>
            </div>
          </div>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:'#C4826A', background:'rgba(196,130,106,0.1)', borderRadius:100, padding:'2px 8px' }}>Premium</span>
        </div>
        {!isPremium ? <PremiumGate navigate={navigate}/> : orders.length === 0 ? (
          <div style={{ textAlign:'center', padding:'2rem', background:'rgba(244,179,0,0.04)', borderRadius:'0.875rem', border:'1px dashed rgba(244,179,0,0.2)' }}>
            <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#C4826A', margin:'0 0 0.25rem' }}>No orders yet</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#BCBCBC', margin:0 }}>Once guests order via the portal they'll appear here.</p>
          </div>
        ) : (
          <AsoEbiOrdersTable orders={orders} points={points} wedding={wedding} asoEbi={setup} updatingOrder={updatingOrder} onUpdateStatus={updateOrderStatus} onSendReminder={sendReminderForOrders}/>
        )}
      </div>

      {/* ── AMBASSADOR ── */}
      {!isPremium ? (
        <PremiumGate navigate={navigate}
          title="Add an Aso Ebi Coordinator"
          desc="Delegate Aso Ebi to a trusted coordinator — they view orders, track payments and send reminders from their own phone, no login needed. Available on Premium."/>
      ) : ambassadors.length > 0 ? (
        <div style={{ background:'rgba(61,139,94,0.06)', border:'1px solid rgba(61,139,94,0.2)', borderRadius:'0.875rem', padding:'1rem 1.125rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#3D8B5E', margin:'0 0 0.25rem' }}>
            ✓ Ambassador{ambassadors.length > 1 ? 's' : ''} assigned
          </p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.35rem' }}>
            {ambassadors.map(a => a.name || 'Ambassador').join(', ')} {ambassadors.length === 1 ? 'has' : 'have'} Aso Ebi coordination access via a secure link.
          </p>
          <button onClick={()=>navigate('/dashboard/settings')} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#B36B55', background:'none', border:'none', cursor:'pointer', padding:0 }}>Manage in Settings →</button>
        </div>
      ) : (
        <div style={{ background:'rgba(244,179,0,0.05)', border:'1px solid rgba(244,179,0,0.2)', borderRadius:'0.875rem', padding:'1rem 1.125rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.35rem' }}>💡 Delegate Aso Ebi coordination</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.6 }}>Ambassadors can view orders, track payments, and send reminders on their own phone — no login needed. They get a secure link via WhatsApp.</p>
          <button onClick={()=>navigate('/dashboard/settings')} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#B36B55', background:'rgba(179,107,85,0.1)', border:'1px solid rgba(179,107,85,0.2)', borderRadius:100, padding:'0.45rem 1rem', cursor:'pointer' }}>+ Add Aso Ebi Ambassador</button>
        </div>
      )}

    </div>
  )
}
